import { Button, Dialog, Flex, Text, createForm } from '@applyboard/crystal-ui';
import { useMemo, useState } from 'react';

/** Form field types for Program Intake form */
export type ProgramIntakeFormFields = {
  programName: string;
  campusName: string;
  intakeTerm: string;
  firstDayOfClass: string;
  lastDayOfClass: string;
  applicationFee: string;
  acceptingApplicationsStartsAt: string;
  acceptingApplicationsStopsAt: string;
  deferralDeadlineAt: string;
  refundDeadlineAt: string;
  tuition: string;
  tuitionPeriod: string;
};

const EDITABLE_FIELD_NAMES: (keyof ProgramIntakeFormFields)[] = [
  'firstDayOfClass',
  'lastDayOfClass',
  'applicationFee',
  'acceptingApplicationsStartsAt',
  'acceptingApplicationsStopsAt',
  'deferralDeadlineAt',
  'refundDeadlineAt',
];

type IntakeFormActionsProps = {
  allowedToEdit: boolean;
  isPatchingIntake: boolean;
  useFieldValues: ReturnType<typeof createForm<ProgramIntakeFormFields>>['useFieldValues'];
  formDefaultValues: ProgramIntakeFormFields;
  setAllowedToEdit: (allowedToEdit: boolean) => void;
  onReset: () => void;
};

function hasFormChanges(
  currentValues: Partial<ProgramIntakeFormFields>,
  defaultValues: ProgramIntakeFormFields
): boolean {
  return EDITABLE_FIELD_NAMES.some((field) => {
    const currentValue = currentValues[field] || '';
    const defaultValue = defaultValues[field] || '';
    return currentValue !== defaultValue;
  });
}

export function IntakeFormActions({
  allowedToEdit,
  isPatchingIntake,
  useFieldValues,
  formDefaultValues,
  setAllowedToEdit,
  onReset,
}: IntakeFormActionsProps) {
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const fieldValues = useFieldValues(EDITABLE_FIELD_NAMES);

  const hasChanges = useMemo(() => {
    return hasFormChanges(fieldValues, formDefaultValues);
  }, [fieldValues, formDefaultValues]);

  const handleCancel = () => {
    if (hasChanges) {
      setShowDiscardModal(true);
    } else {
      setAllowedToEdit(false);
    }
  };

  const handleDiscardConfirm = () => {
    setShowDiscardModal(false);
    onReset();
  };

  return (
    <>
      <Flex justify="end" pt={4}>
        {allowedToEdit ? (
          <Flex gap={2}>
            <Button intent="secondary" emphasis="transparent" onClick={handleCancel}>
              Cancel
            </Button>
            <Button intent="primary" emphasis="contained" type="submit" disabled={isPatchingIntake || !hasChanges}>
              {isPatchingIntake ? 'Saving...' : 'Save Intake'}
            </Button>
          </Flex>
        ) : null}
      </Flex>
      <DiscardChangesModal
        open={showDiscardModal}
        onOpenChange={setShowDiscardModal}
        onConfirm={handleDiscardConfirm}
      />
    </>
  );
}

type DiscardChangesModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
};

function DiscardChangesModal({ open, onOpenChange, onConfirm }: DiscardChangesModalProps) {
  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose} size="md">
      <Dialog.Heading>Discard Changes?</Dialog.Heading>
      <Dialog.Content>
        <Text variant="bodyM">
          You have unsaved changes. <br /> Are you sure you want to discard them?
        </Text>
      </Dialog.Content>
      <Dialog.Actions>
        <Button width="fill" intent="secondary" emphasis="highlighted" onClick={handleClose}>
          Back to editing
        </Button>
        <Button width="fill" intent="negative" onClick={onConfirm}>
          Discard
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
}
